import React, { useState, useCallback, useEffect, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { fetchHardwareDescriptions } from '../../../../../store/slices/hardwareDescriptionSlice';
import * as XLSX from 'xlsx';
import { useCurrencySymbol } from '../../../../../utils/currencyUtils';
import CommaConvert from '../../../../../utils/CommaConvert';
import { formatAmountComma } from '../../../../../utils/decimalAmountUtils';
import {
    canonicalInstallationAssociatedTaskDescription,
    isTrainingExpenseDisplayRow,
    sumInstallationAssociatedTrainingValues,
} from '../../../../../utils/trainingOtherExpenseSync';

const ActualHardwareTable = ({
    matchedProjects,
    currentPerson,
    AssociatedRef,
    items,
    tasks = [],
    selectedProject = null,
}) => {
    const dispatch = useDispatch();
    const currency = useCurrencySymbol();
    const USAComma = CommaConvert();
    const formatMoneyComma = formatAmountComma;
    const { mytrainings } = useSelector((state) => state.myTrainings);
    const [hardwareParts, setHardwareParts] = useState({});

    const trainingRowLabel = useMemo(
        () => canonicalInstallationAssociatedTaskDescription('Training cost', currency),
        [currency]
    );

    const getMyTrainingAmountForAssignee = useMemo(() => {
        const trainingsList = Array.isArray(mytrainings?.trainings)
            ? mytrainings.trainings
            : Array.isArray(mytrainings)
                ? mytrainings
                : [];

        const resolveRefId = (ref) => {
            if (ref == null || ref === '') return '';
            if (typeof ref === 'object') return String(ref?._id ?? ref?.id ?? '').trim();
            return String(ref).trim();
        };

        return (project, assigneeId) => {
            if (!project || !assigneeId) return 0;
            const assignee = resolveRefId(assigneeId);
            if (!assignee) return 0;

            const projectId = String(project?._id || project?.ProjectID || '').trim().toLowerCase();
            const quotationId = String(project?.quotationId || project?.quotationID || project?.quotationid || '').trim();
            const projectName = String(project?.ProjectName || project?.projectname || '').trim().toLowerCase();
            const leadId = String(project?.leadid || project?.leadId || project?.LeadId || project?.LeadID || '').trim();

            const matched = trainingsList.filter((t) => {
                const assignedTo = resolveRefId(t?.assignedto ?? t?.assignedTo);
                if (!assignedTo || assignedTo !== assignee) return false;

                const tProjectId = resolveRefId(t?.projectid ?? t?.projectId ?? t?.project).toLowerCase();
                const tQuotationId = String(t?.quotationId ?? t?.quotationID ?? t?.quotationid ?? '').trim();
                const tProjectName = String(t?.projectname || '').trim().toLowerCase();
                const tLeadId = String(t?.leadid ?? t?.leadId ?? '').trim();

                return (
                    (projectId && tProjectId && projectId === tProjectId) ||
                    (quotationId && tQuotationId && quotationId === tQuotationId) ||
                    (projectName && tProjectName && projectName === tProjectName) ||
                    (leadId && tLeadId && leadId === tLeadId)
                );
            });

            return matched.reduce((sum, t) => {
                const amount = Number(
                    String(t?.trainingcost ?? t?.trainingCost ?? 0)
                        .toString()
                        .replace(/,/g, '')
                );
                return sum + (Number.isFinite(amount) ? amount : 0);
            }, 0);
        };
    }, [mytrainings]);

    const getTaskOwnerId = (task) =>
        task?.taskownerid && typeof task.taskownerid === 'object' && task.taskownerid._id != null
            ? task.taskownerid._id
            : task?.taskownerid;

    const projectCtxForTraining = (person) => ({
        _id: person.ProjectID,
        ProjectID: person.ProjectID,
        ProjectName: selectedProject?.ProjectName,
        quotationId: selectedProject?.quotationId,
        leadid: selectedProject?.leadId ?? selectedProject?.leadid,
    });

    const getMyTrainingSliceForPerson = (person) =>
        getMyTrainingAmountForAssignee(projectCtxForTraining(person), person.personId);

    const getTaskTrainingTotalForPerson = (person) => {
        if (!tasks?.length) return 0;
        const pid = String(person.ProjectID || '').trim();
        const oid = String(person.personId || '').trim();
        const pType = String(person?.typeService || '').trim().toLowerCase();
        return sumInstallationAssociatedTrainingValues(
            tasks.filter((task) => {
                if (String(task?.projectid || '').trim() !== pid) return false;
                if (String(getTaskOwnerId(task) || '').trim() !== oid) return false;
                if (!isTrainingExpenseDisplayRow(task)) return false;
                if (!pType) return true;
                const tType = String(task?.typeservice || '').trim().toLowerCase();
                if (!tType) return true;
                return tType === pType;
            })
        );
    };

    const getTrainingTotalForPerson = (person) =>
        getTaskTrainingTotalForPerson(person) + getMyTrainingSliceForPerson(person);

    useEffect(() => {
        dispatch(fetchHardwareDescriptions());
    }, [dispatch]);

    // Ensure currentPerson is always treated as an array and sort managers first
    const currentPersons = React.useMemo(() => {
        const persons = Array.isArray(currentPerson) ? currentPerson : [currentPerson];

        // Sort: managers first, then operations
        return persons.sort((a, b) => {
            const aIsManager = a.roles?.includes("Project manager");
            const bIsManager = b.roles?.includes("Project manager");

            if (aIsManager && !bIsManager) return -1; // a (manager) comes first
            if (!aIsManager && bIsManager) return 1;  // b (manager) comes first

            // If both are managers or both are operations, maintain original order
            return 0;
        });
    }, [currentPerson]);

    // Group persons by manager for better organization
    const organizedPersons = React.useMemo(() => {
        const managers = currentPersons.filter(p => p.roles?.includes("Project manager"));
        const operations = currentPersons.filter(p => p.roles?.includes("Operations"));

        const result = [...managers]; // Start with managers

        // For each manager, add their operations team members
        managers.forEach(manager => {
            const managerOperations = operations.filter(op =>
                op.ProjectID === manager.ProjectID &&
                op.typeService === manager.typeService
            );

            // Add operations team members after their manager
            result.push(...managerOperations);
        });

        // Add any remaining operations that don't belong to any manager
        const assignedOperationIds = new Set(result.map(p => p.personId));
        const remainingOperations = operations.filter(op => !assignedOperationIds.has(op.personId));
        result.push(...remainingOperations);
        return result;
    }, [currentPersons]);

    // Improved project filtering - match by ProjectID instead of just TypeService
    const filteredProjects = React.useMemo(() => {
        if (!matchedProjects.length) return [];

        const projects = organizedPersons.map(person => {
            // Try to find project by ProjectID first, then fallback to TypeService
            let project = matchedProjects.find(proj => proj._id === person.ProjectID);

            if (!project) {
                // Fallback: find by TypeService
                project = matchedProjects.find(proj =>
                    proj.TypeService === person.typeService
                );
            }
            return project;
        }).filter(Boolean); // Remove undefined values
        return projects;
    }, [matchedProjects, organizedPersons]);

    const getHardwareValues = useCallback(
        (projectID, personID, typeService) => {
            if (!items || !items.length) return [];

            const filteredItems = items.filter(
                (desc) =>
                    desc.projectid === projectID &&
                    desc.taskownerid === personID &&
                    desc.typeservice === typeService
            );
            return filteredItems;
        },
        [items]
    );

    // Get manager and their associated operations personnel with proper numbering
    const getManagerWithTeam = useCallback(() => {
        const persons = Array.isArray(currentPerson) ? currentPerson : [currentPerson];
        const managers = persons.filter(p => p.roles?.includes("Project manager"));
        const operations = persons.filter(p => p.roles?.includes("Operations"));

        const managerTeams = [];

        // Process each manager
        managers.forEach((manager, managerIndex) => {
            const managerNumber = managerIndex + 1;

            // Find operations personnel for this manager (same ProjectID)
            const teamMembers = operations.filter(person =>
                person.ProjectID === manager.ProjectID &&
                person.typeService === manager.typeService
            );
            managerTeams.push({
                manager,
                team: teamMembers,
                managerNumber: managerNumber
            });
        });
        return managerTeams;
    }, [currentPerson]);

    const getPersonDisplayName = useCallback((person, serviceType) => {
        const persons = Array.isArray(currentPerson) ? currentPerson : [currentPerson];

        if (person.roles?.includes("Project manager")) {
            // Find manager number
            const managers = persons.filter(p => p.roles?.includes("Project manager"));
            const managerNumber = managers.findIndex(p => p.personId === person.personId) + 1;
            return `${serviceType} Manager ${managerNumber}`;
        } else if (person.roles?.includes("Operations")) {
            // Find which manager this operations person belongs to and their number within that manager
            const managers = persons.filter(p => p.roles?.includes("Project manager"));
            const operations = persons.filter(p => p.roles?.includes("Operations"));

            // Group operations by manager (same ProjectID)
            const groupedByManager = managers.map(manager => {
                const managerOperations = operations.filter(op => op.ProjectID === manager.ProjectID);
                return {
                    manager,
                    operations: managerOperations
                };
            });

            for (const group of groupedByManager) {
                const operationIndex = group.operations.findIndex(op => op.personId === person.personId);
                if (operationIndex !== -1) {
                    return `${serviceType} Employee${operationIndex + 1}`;
                }
            }

            // Fallback if no manager found
            return `${serviceType} Employee`;
        } else {
            return `${serviceType} ${person.roles?.[0] || ""}`;
        }
    }, [currentPerson]);

    const exportManagerToExcel = (managerData) => {
        const { manager, team } = managerData;

        const managerHardwareParts = hardwareParts[manager.personId] || [];
        const managerName = getPersonDisplayName(manager, manager.typeService);

        const rawList = [];

        // Manager items
        managerHardwareParts.forEach(item => {
            rawList.push({
                taskOwner: managerName,
                ...item
            });
        });

        // Team items
        team.forEach(teamMember => {
            const teamMemberName = getPersonDisplayName(teamMember, teamMember.typeService);
            const teamHardwareParts = hardwareParts[teamMember.personId] || [];

            teamHardwareParts.forEach(item => {
                rawList.push({
                    taskOwner: teamMemberName,
                    ...item
                });
            });
        });

        // STEP 2: group by assemblepartdescription and keep highest totalprice
        const grouped = {};

        rawList.forEach(item => {
            const key = item.assemblepartdescription;

            if (!grouped[key]) {
                grouped[key] = item;
            } else if (item.totalprice > grouped[key].totalprice) {
                grouped[key] = item;
            }
        });

        // STEP 3: final cleaned export list with Sr No
        const allHardwareParts = Object.values(grouped).map((item, index) => ({
            'Sr No': index + 1,  // Add serial number starting from 1
            'Assemble Part Description': item.assemblepartdescription,
            'Unit Price': `${currency}${USAComma(item.unitprice)}`,
            'Quantity': item.quantity,
            'Total Price': `${currency}${USAComma(item.totalprice)}`,
            rawTotalPrice: item.totalprice
        }));

        // Calculate total
        const totalPriceSum = allHardwareParts.reduce(
            (sum, item) => sum + (item.rawTotalPrice || 0),
            0
        );

        const excelData = allHardwareParts.map(({ rawTotalPrice, ...rest }) => rest);

        // Add TOTAL row with empty Sr No column
        excelData.push({
            'Sr No': 'Total',
            'Assemble Part Description': '',
            'Unit Price': '',
            'Quantity': '',
            'Total Price': `${currency}${USAComma(totalPriceSum)}`
        });

        const worksheet = XLSX.utils.json_to_sheet(excelData);
        const workbook = XLSX.utils.book_new();

        const cleanManagerName = managerName.replace(/\s+/g, '_');
        XLSX.utils.book_append_sheet(workbook, worksheet, 'Hardware Parts');
        XLSX.writeFile(workbook, `${cleanManagerName}_Hardware_Parts.xlsx`);
    };

    // Get hardware total by project (Miscellaneous Cost) — same aggregation as Itemized P&L hardware parts
    const getHardwareTotalByProject = useCallback((projectID) => {
        if (!items || !items.length) return 0;
        const pid = String(projectID || "").trim();
        if (!pid) return 0;
        let hardwareValue = 0;
        const filteredItems = items.filter(
            (desc) => String(desc?.projectid || "").trim() === pid
        );

        if (filteredItems?.length > 0) {
            const hardwareByDescription = {};

            // Group hardware by assemblepartdescription and keep only the one with highest totalprice
            filteredItems.forEach((hw) => {
                const description = hw.assemblepartdescription;

                if (!hardwareByDescription[description]) {
                    hardwareByDescription[description] = hw;
                } else {
                    // If we already have this description, keep the one with higher totalprice
                    if (hw.totalprice > hardwareByDescription[description].totalprice) {
                        hardwareByDescription[description] = hw;
                    }
                }
            });

            // Calculate total hardware value using only the highest totalprice items
            Object.values(hardwareByDescription).forEach((hw) => {
                if (hw.totalprice) {
                    hardwareValue += hw.totalprice;
                }
            });
        }
        return hardwareValue;
    }, [items]);

    // Other expenses from description tasks (excludes training — shown on its own row).
    // Do not derive as grandTotal - misc (that zeros manager other expenses when misc > under-counted grandTotal).
    const getAssociatedOtherForPerson = (person) => {
        const desc = Number(person?.descriptionTotal) || 0;
        const train = getTrainingTotalForPerson(person);
        return Math.max(0, desc - train);
    };

    const getSameServicePersons = (person) =>
        organizedPersons.filter(
            (p) =>
                p.ProjectID === person.ProjectID &&
                p.typeService === person.typeService
        );

    // Manager column consolidates manager + ops other expenses; ops column shows "-".
    const getConsolidatedAssociatedOtherForDisplay = (person) => {
        const sameService = getSameServicePersons(person);
        const hasManager = sameService.some((p) =>
            p.roles?.includes('Project manager')
        );
        const isManager = person?.roles?.includes('Project manager');

        if (hasManager && !isManager) return null; // render "-"

        if (isManager) {
            return sameService.reduce(
                (sum, p) => sum + getAssociatedOtherForPerson(p),
                0
            );
        }

        return getAssociatedOtherForPerson(person);
    };

    // Total is common for hardware manager and employee: misc + all other expenses + training.
    const getGrandTotalForPerson = (person) => {
        const sameService = getSameServicePersons(person);
        const misc = getHardwareTotalByProject(person.ProjectID);
        const other = sameService.reduce(
            (sum, p) => sum + getAssociatedOtherForPerson(p),
            0
        );
        const train = sameService.reduce(
            (sum, p) => sum + getTrainingTotalForPerson(p),
            0
        );
        const fromParts = misc + other + train;
        if (fromParts > 0) return fromParts;
        return Number(person?.grandTotal) || 0;
    };

    const hasAnyTrainingRow = organizedPersons.some((p) => getTrainingTotalForPerson(p) > 0);

    // Set hardware parts for all persons in useEffect
    useEffect(() => {
        if (filteredProjects.length > 0 && organizedPersons.length) {
            const partsByPerson = {};

            organizedPersons.forEach(person => {
                // Find the project that matches this person's typeService AND ProjectID
                const personProject = filteredProjects.find(
                    project => project.TypeService === person.typeService && project._id === person.ProjectID
                );

                if (personProject) {
                    const hardwareValues = getHardwareValues(
                        personProject._id,
                        person.personId,
                        person.typeService
                    );
                    partsByPerson[person.personId] = hardwareValues;
                } else {
                    partsByPerson[person.personId] = [];
                }
            });
            setHardwareParts(partsByPerson);
        } else {
            setHardwareParts({});
        }
    }, [filteredProjects, organizedPersons, getHardwareValues]);

    // Get managers with their teams
    const managerTeams = getManagerWithTeam();

    return (
        <div ref={AssociatedRef} className="bg-white pt-5 rounded-md">
            <div className="text-lg font-semibold pb-2">
                <h2 className='pl-2'>Associated Costs: Hardware</h2>
            </div>

            {/* Restructured Table with Proper Alignment */}
            <div className="oldtable p-2">
                <div className="overflow-x-auto ">
                    <div className="flex border-collapse text-xs">

                        {/* Right Side - Data Content */}
                        <div className="flex flex-col  border">

                            {/* Top Row - Person Names */}
                            <div className="flex font-semibold">
                                {/* Total Header */}
                                <div className=" bg-[#e8e8e8] border-b px-2 py-2 h-12 w-40 flex justify-start items-center  text-xs flex-shrink-0">
                                    Task
                                </div>

                                {/* Person Names - Using organizedPersons instead of currentPersons */}
                                {organizedPersons.map((person, index) => {
                                    const personName = getPersonDisplayName(person, person.typeService);
                                    return (
                                        <div key={person.personId || index}
                                            className="personName border-b capitalize px-2 py-2 h-12 w-40 flex justify-center items-center text-wrap text-center text-xs  bg-[#e8e8e8] flex-shrink-0">
                                            {personName}
                                        </div>
                                    );
                                })}
                            </div>

                            {/* Total Row — Miscellaneous Cost + Associated Cost (other expenses) + training */}
                            <div className="flex">
                                <div className="px-2 py-2 h-12 w-40 border-b flex justify-start items-center font-semibold  text-xs flex-shrink-0">
                                    Total ({currency})
                                </div>
                                {organizedPersons.map((person) => (
                                    <div
                                        key={`total-${person.personId}`}
                                        className="px-2 py-2 border-b h-12 w-40 flex justify-center items-center font-semibold text-xs flex-shrink-0"
                                    >
                                        {formatMoneyComma(getGrandTotalForPerson(person))}
                                    </div>
                                ))}
                            </div>

                            {/* Data Rows */}
                            {/* Description Row */}
                            <div className="flex">
                                <div className="px-2 py-2 h-12 w-40 border-b flex justify-start items-center text-xs  flex-shrink-0">
                                    Description
                                </div>
                                {organizedPersons.map((person, index) => (
                                    <div key={`desc-${person.personId}`}
                                        className=" px-2 py-2 h-12  w-40 flex justify-center border-b items-center text-xs flex-shrink-0">
                                        {person.roles?.includes("Project manager") ? (
                                            <div className="flex flex-col gap-1">
                                                <span
                                                    onClick={() => {
                                                        const managerTeam = managerTeams.find(
                                                            mt => mt.manager.personId === person.personId
                                                        );

                                                        if (managerTeam) {
                                                            exportManagerToExcel(managerTeam);
                                                        }
                                                    }}
                                                    className="cursor-pointer text-blue-600  text-xs"
                                                    title={`Export ${getPersonDisplayName(person, person.typeService)} and team data`}
                                                >
                                                    Multiple
                                                </span>
                                            </div>
                                        ) : (
                                            <span className="text-gray-400">-</span>
                                        )}
                                    </div>
                                ))}
                            </div>

                            {/* Miscellaneous Cost Row */}
                            <div className="flex">
                                <div className=" px-2 py-2 h-12 w-40 border-b flex justify-start items-center text-xs  bg-white flex-shrink-0">
                                    Miscellaneous Cost ({currency})
                                </div>
                                {organizedPersons.map((person, index) => {
                                    const isManager = person.roles?.includes("Project manager");
                                    const hardwareTotal = isManager ? getHardwareTotalByProject(person.ProjectID) : 0;

                                    return (
                                        <div key={`misc-${person.personId}`}
                                            className=" bg-white px-2 border-b py-2 h-12 w-40 flex justify-center items-center text-xs flex-shrink-0">
                                            {isManager ? USAComma(hardwareTotal) : "-"}
                                        </div>
                                    );
                                })}
                            </div>

                            {hasAnyTrainingRow && (
                                <div className="flex">
                                    <div className="px-2 py-2 h-12 w-40 border-b flex justify-start items-center text-xs flex-shrink-0 bg-white">
                                        {trainingRowLabel}
                                    </div>
                                    {organizedPersons.map((person) => {
                                        const n = getTrainingTotalForPerson(person);
                                        return (
                                            <div
                                                key={`train-${person.personId}`}
                                                className="px-2 py-2 border-b font-medium h-12 w-40 flex justify-center items-center text-xs flex-shrink-0 bg-white"
                                            >
                                                {n > 0 ? formatMoneyComma(n) : '-'}
                                            </div>
                                        );
                                    })}
                                </div>
                            )}

                            {/* Associated Cost Row — manager consolidates manager+ops other expenses; ops shows "-" */}
                            <div className="flex">
                                <div className="px-2 py-2 h-12 w-40 border-b flex justify-start items-center text-xs  flex-shrink-0">
                                    Associated Cost ({currency})
                                </div>
                                {organizedPersons.map((person) => {
                                    const consolidatedOther =
                                        getConsolidatedAssociatedOtherForDisplay(person);
                                    return (
                                        <div
                                            key={`assoc-${person.personId}`}
                                            className="px-2 py-2 border-b font-medium h-12 w-40 flex justify-center items-center text-xs flex-shrink-0"
                                        >
                                            {consolidatedOther == null
                                                ? "-"
                                                : formatMoneyComma(consolidatedOther)}
                                        </div>
                                    );
                                })}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    )
}

export default ActualHardwareTable;